import apiFetch from "../../../shared/services/apiClient";

// ── Notificaciones ────────────────────────────────────────────────────────────

export async function getNotificaciones() {
  const res = await apiFetch("/api/core/portal/notificaciones/");
  if (!res.ok) throw new Error("Error al cargar notificaciones");
  return res.json();
}

export async function marcarTodasLeidas() {
  await apiFetch("/api/core/portal/notificaciones/", { method: "PATCH" });
}

// Devuelve el aviso del servidor cuando la notificación no es del condominio
// activo (409), para no mandar a la persona a una pantalla que no la encuentra.
export async function marcarLeida(pk) {
  const res = await apiFetch(`/api/core/portal/notificaciones/${pk}/`, { method: "PATCH" });
  if (res.status === 409) {
    const d = await res.json().catch(() => ({}));
    return { otroCondominio: true, detalle: d.detail || "Esa notificación es de otro condominio." };
  }
  return { otroCondominio: false };
}

export async function borrarNotificacion(pk) {
  const res = await apiFetch(`/api/core/portal/notificaciones/${pk}/`, { method: "DELETE" });
  if (!res.ok && res.status !== 404) throw new Error("No se pudo borrar la notificación");
}

export async function borrarTodasNotificaciones() {
  const res = await apiFetch("/api/core/portal/notificaciones/", { method: "DELETE" });
  if (!res.ok) throw new Error("No se pudieron borrar las notificaciones");
}

// ── Estado de cuenta en PDF (residente) ──────────────────────────────────────
// La filial la pone el servidor (la del residente). Límite de PDFs por hora:
// el 429 llega con un mensaje para mostrar.
export async function descargarEstadoCuentaPDF({ desde, hasta, moneda = "CRC", idioma = "es" }) {
  const qs = new URLSearchParams({ desde, hasta, moneda, idioma });
  const res = await apiFetch(`/api/core/portal/estado-cuenta/pdf/?${qs}`);
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    if (res.status === 429) throw new Error("Ya descargaste varios estados de cuenta seguidos. Esperá un rato e intentá de nuevo.");
    throw new Error(d.detail || "No se pudo generar el estado de cuenta.");
  }
  const blob = await res.blob();
  const nombre = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") || "")?.[1]
    || `estado_cuenta_${desde}_${hasta}.pdf`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── Estado de cuenta ──────────────────────────────────────────────────────────

export async function getEstadoCuenta() {
  const res = await apiFetch("/api/core/portal/estado-cuenta/");
  if (!res.ok) throw new Error("Error al cargar estado de cuenta");
  return res.json();
}

// ── Áreas comunes ─────────────────────────────────────────────────────────────

export async function getAreas() {
  const res = await apiFetch("/api/core/portal/areas/");
  if (!res.ok) throw new Error("Error al cargar áreas");
  return res.json();
}

export async function createArea(formData) {
  const res = await apiFetch("/api/core/portal/areas/", { method: "POST", body: formData });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(JSON.stringify(d)); }
  return res.json();
}

export async function updateArea(pk, formData) {
  const res = await apiFetch(`/api/core/portal/areas/${pk}/`, { method: "PATCH", body: formData });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(JSON.stringify(d)); }
  return res.json();
}

export async function deleteArea(pk) {
  const res = await apiFetch(`/api/core/portal/areas/${pk}/`, { method: "DELETE" });
  if (!res.ok) throw new Error("Error al eliminar área");
}

// ── Horarios ──────────────────────────────────────────────────────────────────

export async function createHorario(data) {
  const res = await apiFetch("/api/core/portal/horarios/", {
    method: "POST",
    body: JSON.stringify(data),
  });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(JSON.stringify(d)); }
  return res.json();
}

export async function updateHorario(pk, data) {
  const res = await apiFetch(`/api/core/portal/horarios/${pk}/`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(JSON.stringify(d)); }
  return res.json();
}

export async function deleteHorario(pk) {
  const res = await apiFetch(`/api/core/portal/horarios/${pk}/`, { method: "DELETE" });
  if (!res.ok) throw new Error("Error al eliminar horario");
}

// ── Disponibilidad ────────────────────────────────────────────────────────────

export async function getDisponibilidad(areaId, year, month) {
  const res = await apiFetch(`/api/core/portal/disponibilidad/?area=${areaId}&year=${year}&month=${month}`);
  if (!res.ok) throw new Error("Error al cargar disponibilidad");
  return res.json();
}

// ── Reservaciones ─────────────────────────────────────────────────────────────

export async function getReservaciones(params = {}) {
  const qs = new URLSearchParams(params).toString();
  const res = await apiFetch(`/api/core/portal/reservaciones/${qs ? "?" + qs : ""}`);
  if (!res.ok) throw new Error("Error al cargar reservaciones");
  return res.json();
}

export async function crearReservacion(formData) {
  const res = await apiFetch("/api/core/portal/reservaciones/", { method: "POST", body: formData });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(JSON.stringify(d)); }
  return res.json();
}

export async function revisarReservacion(pk, payload) {
  const res = await apiFetch(`/api/core/portal/reservaciones/${pk}/`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(JSON.stringify(d)); }
  return res.json();
}

export async function cambiarFechaReservacion(pk, nueva_fecha) {
  const res = await apiFetch(`/api/core/portal/reservaciones/${pk}/cambiar-fecha/`, {
    method: "PATCH",
    body: JSON.stringify({ nueva_fecha }),
  });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(JSON.stringify(d)); }
  return res.json();
}

// ── Parámetros de reservación ─────────────────────────────────────────────────
// El admin recibe las reglas completas; el residente, solo hasta cuándo puede
// reservar y si está bloqueado por mora.

export async function getParametrosReservacion() {
  const res = await apiFetch("/api/core/portal/reservaciones/parametros/");
  if (!res.ok) throw new Error("Error al cargar los parámetros de reservación");
  return res.json();
}

export async function guardarParametrosReservacion(data) {
  const res = await apiFetch("/api/core/portal/reservaciones/parametros/", {
    method: "PATCH",
    body: JSON.stringify(data),
  });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(JSON.stringify(d)); }
  return res.json();
}

/** Estado de mora de una filial, para avisarle al admin antes de registrar. */
export async function getMoraFilial(filialId) {
  const res = await apiFetch(`/api/core/portal/reservaciones/mora/?filial=${filialId}`);
  if (!res.ok) throw new Error("Error al consultar el estado de la filial");
  return res.json();
}

export async function getCuentasIngreso() {
  const res = await apiFetch("/api/core/portal/cuentas-ingreso/");
  if (!res.ok) throw new Error("Error al cargar cuentas de ingreso");
  return res.json();
}

// Cuentas bancarias del condominio, para registrar dónde entró el pago de una
// reserva. El endpoint pagina de 8 en 8, así que se recorren todas las páginas.
export async function getCuentasBancarias() {
  const todas = [];
  for (let page = 1; page <= 20; page++) {
    const res = await apiFetch(`/api/finance/cuentaBancaria/?page=${page}`);
    if (!res.ok) { if (page === 1) throw new Error("Error al cargar cuentas bancarias"); break; }
    const d = await res.json();
    if (Array.isArray(d)) return d;
    todas.push(...(d.results || []));
    if (!d.next) break;
  }
  return todas;
}

export async function cancelarReservacion(pk) {
  const res = await apiFetch(`/api/core/portal/reservaciones/${pk}/`, {
    method: "PATCH",
    body: JSON.stringify({ estado: "CANCELADA" }),
  });
  if (!res.ok) throw new Error("Error al cancelar reservación");
}

export async function deleteReservacion(pk) {
  const res = await apiFetch(`/api/core/portal/reservaciones/${pk}/`, { method: "DELETE" });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || "Error al eliminar la reservación"); }
}

// ── Avisos de pago ────────────────────────────────────────────────────────────

export async function getAvisosPago(params = {}) {
  const qs = new URLSearchParams(params).toString();
  const res = await apiFetch(`/api/core/portal/avisos-pago/${qs ? "?" + qs : ""}`);
  if (!res.ok) throw new Error("Error al cargar avisos de pago");
  return res.json();
}

export async function crearAvisoPago(formData) {
  const res = await apiFetch("/api/core/portal/avisos-pago/", { method: "POST", body: formData });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(JSON.stringify(d)); }
  return res.json();
}

export async function revisarAvisoPago(pk, estado, notas_admin = "") {
  const res = await apiFetch(`/api/core/portal/avisos-pago/${pk}/`, {
    method: "PATCH",
    body: JSON.stringify({ estado, notas_admin }),
  });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || JSON.stringify(d)); }
  return res.json();
}

export async function deleteAvisoPago(pk) {
  const res = await apiFetch(`/api/core/portal/avisos-pago/${pk}/`, { method: "DELETE" });
  if (!res.ok) throw new Error("Error al eliminar aviso de pago");
}

// ── Avisos admin ──────────────────────────────────────────────────────────────

export async function getAvisos(params = {}) {
  const qs = new URLSearchParams(params).toString();
  const res = await apiFetch(`/api/core/portal/avisos/${qs ? "?" + qs : ""}`);
  if (!res.ok) throw new Error("Error al cargar avisos");
  return res.json();
}

/**
 * Error de una subida. El 413 lo corta el servidor web antes de llegar a
 * Django, así que la respuesta es HTML y no JSON: sin este caso el toast salía
 * vacío y el usuario no sabía que el problema era el peso.
 */
async function errorDeSubida(res, respaldo) {
  if (res.status === 413) {
    return new Error(
      "Los archivos son demasiado pesados para el servidor. Probá con menos " +
      "archivos, o subilos a un enlace y pegá el enlace en el mensaje."
    );
  }
  const cuerpo = await res.json().catch(() => null);
  if (cuerpo) return new Error(JSON.stringify(cuerpo));
  return new Error(respaldo);
}

export async function crearAviso(formData) {
  const res = await apiFetch("/api/core/portal/avisos/", { method: "POST", body: formData });
  if (!res.ok) throw await errorDeSubida(res, `No se pudo enviar el aviso (error ${res.status}).`);
  return res.json();
}

export async function editarAviso(pk, formData) {
  const res = await apiFetch(`/api/core/portal/avisos/${pk}/`, { method: "PATCH", body: formData });
  if (!res.ok) throw await errorDeSubida(res, `No se pudo editar el aviso (error ${res.status}).`);
  return res.json();
}

export async function deleteAviso(pk) {
  const res = await apiFetch(`/api/core/portal/avisos/${pk}/`, { method: "DELETE" });
  if (!res.ok) throw new Error("Error al eliminar aviso");
}

// ── Residentes ────────────────────────────────────────────────────────────────

export async function getResidentes() {
  const res = await apiFetch("/api/core/portal/residentes/");
  if (!res.ok) throw new Error("Error al cargar residentes");
  return res.json();
}

export async function crearResidente(data) {
  const res = await apiFetch("/api/core/portal/residentes/", {
    method: "POST",
    body: JSON.stringify(data),
  });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(JSON.stringify(d)); }
  return res.json();
}

export async function updateResidente(pk, data) {
  const res = await apiFetch(`/api/core/portal/residentes/${pk}/`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(JSON.stringify(d)); }
  return res.json();
}

export async function deleteResidente(pk) {
  const res = await apiFetch(`/api/core/portal/residentes/${pk}/`, { method: "DELETE" });
  if (!res.ok) throw new Error("Error al eliminar residente");
}

// ── Invitaciones y restablecer contraseña ────────────────────────────────────

async function _json(res, fallback) {
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.detail || fallback);
  return d;
}

export async function getInvitaciones() {
  return _json(await apiFetch("/api/core/portal/invitaciones/"), "Error al cargar invitaciones");
}

export async function crearInvitacion(filial_id, email) {
  return _json(await apiFetch("/api/core/portal/invitaciones/", {
    method: "POST",
    body: JSON.stringify({ filial_id, email }),
  }), "No se pudo crear la invitación");
}

/** Máximo 10 por llamada: el componente manda la selección en lotes. */
export async function invitarMasivo(items) {
  return _json(await apiFetch("/api/core/portal/invitaciones/masivo/", {
    method: "POST",
    body: JSON.stringify({ items }),
  }), "No se pudieron enviar las invitaciones");
}

export async function reenviarInvitacion(pk) {
  return _json(await apiFetch(`/api/core/portal/invitaciones/${pk}/reenviar/`, { method: "POST" }),
    "No se pudo reenviar la invitación");
}

export async function cancelarInvitacion(pk) {
  return _json(await apiFetch(`/api/core/portal/invitaciones/${pk}/`, { method: "DELETE" }),
    "No se pudo cancelar la invitación");
}

export async function enviarResetResidente(pk) {
  return _json(await apiFetch(`/api/core/portal/residentes/${pk}/enviar-reset/`, { method: "POST" }),
    "No se pudo enviar el enlace");
}

// ── Tickets ───────────────────────────────────────────────────────────────────
// El residente abre tickets y sigue el hilo; la administración responde y los
// mueve de estado. Los dos usan los mismos endpoints: el backend decide qué
// devuelve según el rol.

/** Texto legible del error del backend, que puede venir como detail o por campo. */
async function errorTicket(res, respaldo) {
  if (res.status === 413) {
    return new Error(
      "Los archivos son demasiado pesados para el servidor. Probá con menos archivos."
    );
  }
  const d = await res.json().catch(() => null);
  if (!d) return new Error(respaldo);
  if (d.detail) return new Error(d.detail);
  const partes = Object.values(d).flat().filter(Boolean);
  return new Error(partes.length ? partes.join(". ") : respaldo);
}

export async function getTickets(params = {}) {
  const limpios = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== "" && v != null)
  );
  const qs = new URLSearchParams(limpios).toString();
  const res = await apiFetch(`/api/core/portal/tickets/${qs ? "?" + qs : ""}`);
  if (!res.ok) throw await errorTicket(res, "No se pudieron cargar los tickets.");
  return res.json();
}

/** El ticket con el hilo completo. Abrirlo lo marca como leído de ese lado. */
export async function getTicket(pk) {
  const res = await apiFetch(`/api/core/portal/tickets/${pk}/`);
  if (!res.ok) throw await errorTicket(res, "No se pudo abrir el ticket.");
  return res.json();
}

export async function crearTicket(formData) {
  const res = await apiFetch("/api/core/portal/tickets/", { method: "POST", body: formData });
  if (!res.ok) throw await errorTicket(res, "No se pudo abrir el ticket.");
  return res.json();
}

export async function responderTicket(pk, formData) {
  const res = await apiFetch(`/api/core/portal/tickets/${pk}/mensajes/`, {
    method: "POST",
    body: formData,
  });
  if (!res.ok) throw await errorTicket(res, "No se pudo enviar el mensaje.");
  return res.json();
}

/**
 * Cambia el estado o la categoría (administración), o reabre el ticket
 * (residente, mandando estado ABIERTO y nada más).
 */
export async function actualizarTicket(pk, payload) {
  const res = await apiFetch(`/api/core/portal/tickets/${pk}/`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await errorTicket(res, "No se pudo actualizar el ticket.");
  return res.json();
}

export async function deleteTicket(pk) {
  const res = await apiFetch(`/api/core/portal/tickets/${pk}/`, { method: "DELETE" });
  if (!res.ok) throw await errorTicket(res, "No se pudo eliminar el ticket.");
}

// ── Categorías de tickets ─────────────────────────────────────────────────────

export async function getCategoriasTicket() {
  const res = await apiFetch("/api/core/portal/tickets/categorias/");
  if (!res.ok) throw await errorTicket(res, "No se pudieron cargar las categorías.");
  return res.json();
}

export async function crearCategoriaTicket(nombre) {
  const res = await apiFetch("/api/core/portal/tickets/categorias/", {
    method: "POST",
    body: JSON.stringify({ nombre }),
  });
  if (!res.ok) throw await errorTicket(res, "No se pudo crear la categoría.");
  return res.json();
}

export async function actualizarCategoriaTicket(pk, payload) {
  const res = await apiFetch(`/api/core/portal/tickets/categorias/${pk}/`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await errorTicket(res, "No se pudo guardar la categoría.");
  return res.json();
}

export async function deleteCategoriaTicket(pk) {
  const res = await apiFetch(`/api/core/portal/tickets/categorias/${pk}/`, { method: "DELETE" });
  if (!res.ok) throw await errorTicket(res, "No se pudo borrar la categoría.");
}
